import { useEffect } from 'react'

import type { UseFormPayload } from '@domain-group/react-hooks'
import { useSetState } from '@domain-group/react-hooks'

import useBrandGroupProvider, { useBrandGroupProviderForm } from '@hooks/useBrandGroupProvider'
import useBrandGroups from '@hooks/useBrandGroups'
import useBrandProvider from '@hooks/useBrandProvider'

import type { BrandModel, BrandGroupProviderModel } from '@models/types'

type DefaultState = {
  brandId?: number,
}

type SetState = (state: Partial<DefaultState>) => void

type HandleBrandSelectorParams = {
  brand?: BrandModel,
  setState: SetState,
  setEntityState: UseFormPayload['setEntityState'],
}

const handleBrandSelector = (params: HandleBrandSelectorParams) => {
  const { brand, setState, setEntityState } = params

  if (!brand){
    setEntityState({ brand_group_id: undefined })
    // @ts-expect-error TS2322
    setState({ brandId: null })
    return
  }

  setState({ brandId: brand.id })
}

const defaultState: DefaultState = {
  brandId: undefined,
}

type UseCreateBrandGroupProviderModalParams = {
  brandGroupProvider: Partial<BrandGroupProviderModel>,
  brandId?: number,
  closeModal: () => void,
}

const useCreateBrandGroupProviderModal = (params: UseCreateBrandGroupProviderModalParams) => {
  const { brandGroupProvider, brandId: initBrandId, closeModal } = params

  const [state, setState] = useSetState(defaultState)
  const { brandId } = state

  useEffect(() => {
    if (initBrandId){
      setState({ brandId: initBrandId })
    }
  }, [initBrandId])

  const {
    brandGroup,
    callbacks: { createBrandGroupProvider: createFn },
    creating,
    provider,
  } = useBrandGroupProvider(brandGroupProvider)

  const formPayload = useBrandGroupProviderForm(brandGroupProvider, { validateOn: 'change' })
  const {
    entityState,
    handlers,
    saveEnabled,
    setEntityState,
  } = formPayload

  const {
    callbacks: { addBrandProvider },
  } = useBrandProvider({ brand_id: brandGroup.brand_id })

  const brandGroupsPayload = useBrandGroups({
    filters: {
      brand_id: brandId,
    },
    performHttpRequests: !!brandId,
    requestOptions: {
      include_brand_group_provider_ids: true,
    },
    skipLoadedForKeysCheck: true,
  })

  const {
    filteredBrandGroups,
  } = brandGroupsPayload

  // @ts-expect-error TS2339
  const unassignedBrandGroups = filteredBrandGroups.filter(bg => !bg.provider_ids?.includes(provider.id))

  const entityText = brandId ? 'Brand Group' : 'Provider'

  const createBrandGroupProviderPayload = {
    callbacks: {
      action: createFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `${entityText} Added`,
  }

  return {
    callbacks: {
      addBrandProvider,
      handleBrandSelector: (brand?: BrandModel) => handleBrandSelector({ brand, setState, setEntityState }),
    },
    brandGroup,
    brandId,
    createBrandGroupProviderPayload,
    creating,
    entityState,
    entityText,
    formPayload,
    handlers,
    saveEnabled,
    unassignedBrandGroups,
  }
}

export default useCreateBrandGroupProviderModal
